(() => {
  let modalReturnFocus = null;
  let modalAutoOpenTimer = null;

  const getModal = () => document.querySelector('[data-skinny-modal], #signupModal');
  const getOpenMenu = () => document.querySelector('[data-skinny-menu-panel].open, #menuPanel.open');

  const getHeaderRoot = (element) => element?.closest('[data-skinny-home-header], [data-skinny-reference-home]');

  const getMenuElements = (root) => ({
    toggle: root?.querySelector('[data-skinny-menu-toggle], #menuToggle'),
    panel: root?.querySelector('[data-skinny-menu-panel], #menuPanel'),
    close: root?.querySelector('[data-skinny-menu-close], #menuClose'),
  });

  const openMenu = (root) => {
    const { toggle, panel, close } = getMenuElements(root);
    if (!panel || !toggle) return;
    panel.classList.add('open');
    toggle.setAttribute('aria-expanded', 'true');
    close?.focus();
  };

  const closeMenu = (panel = getOpenMenu(), restoreFocus = false) => {
    if (!panel) return;
    const root = getHeaderRoot(panel);
    const { toggle } = getMenuElements(root);
    panel.classList.remove('open');
    toggle?.setAttribute('aria-expanded', 'false');
    if (restoreFocus) toggle?.focus();
  };

  const openModal = (trigger) => {
    const modal = getModal();
    if (!modal) return;
    modalReturnFocus = trigger || document.activeElement;
    modal.classList.add('open');
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('no-scroll');
    window.setTimeout(() => modal.querySelector('[data-skinny-modal-email], #emailInput')?.focus(), 0);
  };

  const closeModal = () => {
    const modal = getModal();
    if (!modal) return;
    modal.classList.remove('open');
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('no-scroll');
    if (modalReturnFocus instanceof HTMLElement) modalReturnFocus.focus();
  };

  const syncBackToTop = () => {
    document.querySelectorAll('[data-skinny-back-to-top], #BackToTop').forEach((button) => {
      button.classList.toggle('is-visible', window.scrollY > 420);
    });
  };

  const updateCartCount = (count) => {
    const parsedCount = Number.parseInt(count, 10);
    if (!Number.isFinite(parsedCount)) return;
    document.querySelectorAll('[data-skinny-cart-count]').forEach((element) => {
      element.textContent = String(parsedCount);
    });
  };

  const getCartCountFromUpdate = (event) => {
    if (Number.isFinite(event?.cartData?.item_count)) return event.cartData.item_count;
    const cartBubbleSection = event?.cartData?.sections?.['cart-icon-bubble'];
    if (!cartBubbleSection) return null;
    const sectionDocument = new DOMParser().parseFromString(cartBubbleSection, 'text/html');
    return sectionDocument.querySelector('.cart-count-bubble [aria-hidden="true"]')?.textContent?.trim() || 0;
  };

  const scheduleAutomaticModal = (scope = document) => {
    const modal = scope.matches?.('[data-skinny-modal], #signupModal')
      ? scope
      : scope.querySelector?.('[data-skinny-modal], #signupModal');
    if (!modal || modal.dataset.autoOpenInitialized === 'true') return;
    modal.dataset.autoOpenInitialized = 'true';

    const autoOpen = modal.dataset.autoOpen !== 'false';
    const delay = Number.parseInt(modal.dataset.autoOpenDelay || '1800', 10);
    const storageKey = modal.dataset.storageKey || 'skinnyModalShown';
    if (modal.querySelector('.form-status')) {
      openModal();
      return;
    }
    if (!autoOpen || window.Shopify?.designMode) return;

    window.clearTimeout(modalAutoOpenTimer);
    modalAutoOpenTimer = window.setTimeout(() => {
      try {
        if (!window.sessionStorage.getItem(storageKey)) {
          openModal();
          window.sessionStorage.setItem(storageKey, '1');
        }
      } catch (_error) {
        // Storage can be unavailable in privacy modes; the page remains fully usable.
      }
    }, Number.isFinite(delay) ? delay : 1800);
  };

  const initialize = (scope = document) => {
    scheduleAutomaticModal(scope);
    syncBackToTop();
  };

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const menuToggle = target.closest('[data-skinny-menu-toggle], #menuToggle');
    if (menuToggle) {
      const root = getHeaderRoot(menuToggle);
      const { panel } = getMenuElements(root);
      if (panel?.classList.contains('open')) closeMenu(panel);
      else openMenu(root);
      return;
    }

    const menuClose = target.closest('[data-skinny-menu-close], #menuClose');
    if (menuClose) {
      closeMenu(getMenuElements(getHeaderRoot(menuClose)).panel, true);
      return;
    }

    const menuLink = target.closest('[data-skinny-menu-panel] a, #menuPanel a');
    if (menuLink) closeMenu(menuLink.closest('[data-skinny-menu-panel], #menuPanel'));

    const modalTrigger = target.closest('[data-skinny-modal-trigger], #memberPopup, #earlyPopup');
    if (modalTrigger) {
      openModal(modalTrigger);
      return;
    }

    const modalClose = target.closest('[data-skinny-modal-close], #modalClose');
    if (modalClose) {
      closeModal();
      return;
    }

    const backToTop = target.closest('[data-skinny-back-to-top], #BackToTop');
    if (backToTop) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const modal = getModal();
    if (modal?.classList.contains('open') && target === modal) closeModal();

    const openPanel = getOpenMenu();
    if (openPanel) {
      const root = getHeaderRoot(openPanel);
      const { toggle } = getMenuElements(root);
      if (!openPanel.contains(target) && !toggle?.contains(target)) closeMenu(openPanel);
    }
  });

  document.addEventListener('keydown', (event) => {
    const modal = getModal();
    if (event.key === 'Tab' && modal?.classList.contains('open')) {
      const focusable = Array.from(
        modal.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])')
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (focusable.length && event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
        return;
      }

      if (focusable.length && !event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
        return;
      }
    }

    if (event.key !== 'Escape') return;
    if (modal?.classList.contains('open')) closeModal();
    else closeMenu(getOpenMenu(), true);
  });

  window.addEventListener('scroll', syncBackToTop, { passive: true });
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => updateCartCount(getCartCountFromUpdate(event)));
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize());
  else initialize();
})();
